import { useCallback, useSyncExternalStore } from "react";
import { useStore } from "react-redux";
import type { RootState } from "@Reducers/index";

/*
 * The one switch between the classic UI and the new ("next") UI. Web-only: nothing that mobile
 * bundles may import this file (reducers, actions, the services mobile uses, portalChat core/api/hooks).
 *
 * Off by default. In the allowed environments only, `?ui=next|classic` and `?viewer=classic|next` are read once,
 * synchronously, when this module is first evaluated (Router imports it), stored, and removed from the address bar. Every access
 * to window, storage, history and the document is wrapped, so a missing or blocked API (private mode,
 * React Native, Node) means "classic" and never a throw at module evaluation.
 *
 * Per user (plan-ds §4.1): the stored record is bound to the first signed-in user who loads it, and the gate reads
 * the signed-in user straight from the store (`userDetails._id`), so another user on the same browser never renders
 * the new UI, not even for one frame. While the user is not known yet, a record bound to someone stays "pending"
 * (Router renders neither tree on a shell route). The binding itself is written by the store subscription of the
 * gate hooks (Router always runs one), on every host.
 *
 * Outside the allowed environments the hooks subscribe to nothing and compute nothing. A mode written by another
 * tab is not applied live (it would remount this tab's page and lose its state): it takes effect on the next load.
 */

export type UiMode = "next" | "classic";

/** The localStorage record behind the flag. `userId` null = not bound yet; the next user to load binds it. */
export interface StoredUiMode {
  mode: UiMode;
  userId: string | null;
}

/** What `?ui=` / `?viewer=` asked for, and the query string with those two params removed. */
export interface UiUrlParams {
  mode: UiMode | null;
  viewer: UiMode | null;
  search: string;
}

/**
 * The new UI for this render: `on`, `off`, or `pending` (a stored "next" record whose owner cannot be checked yet
 * because the signed-in user has not loaded). Pending renders as classic everywhere except Router's shell routes,
 * which render nothing until the gate settles.
 */
export type UiNextGate = "on" | "off" | "pending";

/** Environments where the new UI may be switched on. Array `.includes`, never `===` against the env value:
 *  env is injected at runtime by placeholder + sed, and the minifier folds `===` (same pattern as SignupFlow/EnterEmail.tsx). */
const ALLOWED_ENVS = ["development", "qa", "staging"];

const MODE_KEY = "cube.ui.mode";
const VIEWER_KEY = "cube.ui.viewer";
const MODE_PARAM = "ui";
const VIEWER_PARAM = "viewer";
const CLASSIC: StoredUiMode = { mode: "classic", userId: null };

function toUiMode(value: unknown): UiMode | null {
  return value === "next" || value === "classic" ? value : null;
}

/** Pure: a raw `cube.ui.mode` value → a valid record. Anything unreadable is classic and unbound. */
export function parseStoredUiMode(raw: string | null): StoredUiMode {
  if (raw === null) return CLASSIC;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || !("mode" in parsed)) return CLASSIC;
    const mode = toUiMode(parsed.mode);
    if (mode === null) return CLASSIC;
    const userId =
      "userId" in parsed && typeof parsed.userId === "string" && parsed.userId !== "" ? parsed.userId : null;
    return { mode, userId };
  } catch {
    return CLASSIC;
  }
}

/**
 * Pure: a raw `cube.ui.viewer` value → the viewer mode. The viewer follows the UI mode by default (plan-ds §4.1,
 * user decision 2026-10-10): only an explicit `classic` (written by `?viewer=classic`) opts out of it.
 */
export function parseStoredViewerMode(raw: string | null): UiMode {
  return raw === "classic" ? "classic" : "next";
}

/** Pure: the new viewer renders when the new UI is on and the viewer has not been opted out (`?viewer=classic`). */
export function viewerFollowsUi(uiNext: boolean, viewerMode: UiMode): boolean {
  return uiNext && viewerMode !== "classic";
}

/** Pure: reads `?ui=` and `?viewer=` and returns the query string without them. Only the recognised
 *  values (`next`, `classic`) are taken; any other value is left in place, untouched. */
export function takeUiParams(search: string): UiUrlParams {
  const params = new URLSearchParams(search);
  const mode = toUiMode(params.get(MODE_PARAM));
  const viewer = toUiMode(params.get(VIEWER_PARAM));
  if (mode === null && viewer === null) return { mode, viewer, search };
  if (mode !== null) params.delete(MODE_PARAM);
  if (viewer !== null) params.delete(VIEWER_PARAM);
  const rest = params.toString();
  return { mode, viewer, search: rest === "" ? "" : `?${rest}` };
}

/**
 * Pure: does a stored record belong to this session's user? A known user matches their own record or an unbound
 * one. An unknown user (null: not loaded yet, or signed out) matches only an unbound record: a record bound to
 * someone is never assumed to be theirs.
 */
export function uiModeMatchesUser(stored: StoredUiMode, sessionUserId: string | null): boolean {
  if (sessionUserId === null) return stored.userId === null;
  return stored.userId === null || stored.userId === sessionUserId;
}

/**
 * Pure: the gate for a stored record, the session user (null = not known yet) and whether this page load carried
 * `?ui=` (`carried`).
 * - a classic record → off;
 * - a known user → on for their own or an unbound record, off for another user's;
 * - an unknown user → on only for an unbound record this very load carried (the QA hand-off, the sign-in host's
 *   upgrade round trip); any other "next" record is pending until the user is known.
 */
export function resolveUiNextGate(stored: StoredUiMode, sessionUserId: string | null, carried: boolean): UiNextGate {
  if (stored.mode !== "next") return "off";
  if (sessionUserId !== null) return uiModeMatchesUser(stored, sessionUserId) ? "on" : "off";
  return stored.userId === null && carried ? "on" : "pending";
}

/**
 * Pure: the record after `bindUiModeToUser(userId)`. An unbound classic record (also what "nothing stored" parses
 * to) is returned as it is: classic is off for every user, so there is nothing to bind, and a user who never
 * switched must not get a `cube.ui.mode` record written for them (`uiModeHandoff` keys on its absence).
 */
export function bindStoredUiMode(stored: StoredUiMode, userId: string): StoredUiMode {
  if (stored.userId === null && stored.mode === "classic") return stored;
  if (stored.userId === null) return { mode: stored.mode, userId };
  if (stored.userId === userId) return stored;
  return { mode: "classic", userId };
}

export function isUiNextAllowed(): boolean {
  try {
    return ALLOWED_ENVS.includes(process.env.REACT_APP_ENV || "");
  } catch {
    return false;
  }
}

/** Portal kill switch: `layout.UI_NEXT.ENABLE === false` (optional metaData key; absent = allowed). */
export function isUiNextKilled(layout: unknown): boolean {
  if (typeof layout !== "object" || layout === null || !("UI_NEXT" in layout)) return false;
  const uiNext: unknown = layout.UI_NEXT;
  if (typeof uiNext !== "object" || uiNext === null || !("ENABLE" in uiNext)) return false;
  return uiNext.ENABLE === false;
}

interface UiState {
  stored: StoredUiMode;
  viewer: UiMode;
  sessionUserId: string | null;
  /** This page load's URL carried `?ui=` (see `resolveUiNextGate`). */
  carried: boolean;
}

const listeners = new Set<() => void>();
let state: UiState = { stored: CLASSIC, viewer: "next", sessionUserId: null, carried: false };

function readKey(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage blocked or full: the in-memory value still applies for this page load.
  }
}

function isLocallyOn(current: UiState): boolean {
  return isUiNextAllowed() && resolveUiNextGate(current.stored, current.sessionUserId, current.carried) === "on";
}

/** html[data-ui="next"] while the flag is on; no attribute at all while it is off. The portal kill switch
 *  is not reflected here: every skin keyed on it also needs an opt-in attribute only V2 wrappers render. */
function syncHtmlAttribute(): void {
  try {
    const root = document.documentElement;
    if (isLocallyOn(state)) root.setAttribute("data-ui", "next");
    else root.removeAttribute("data-ui");
  } catch {
    // No DOM (tests, React Native).
  }
}

function commit(next: UiState): void {
  state = next;
  syncHtmlAttribute();
  listeners.forEach((listener) => listener());
}

function persistMode(stored: StoredUiMode): void {
  writeKey(MODE_KEY, JSON.stringify(stored));
}

function loadFromStorage(): void {
  state = {
    stored: parseStoredUiMode(readKey(MODE_KEY)),
    viewer: parseStoredViewerMode(readKey(VIEWER_KEY)),
    sessionUserId: state.sessionUserId,
    carried: state.carried,
  };
}

/** Outside the allowed environments (prod, BPCL, MP) `?ui=` / `?viewer=` are ignored entirely: nothing is
 *  stored and the address bar is left as it is. */
function applyUrlParams(): void {
  if (!isUiNextAllowed()) return;
  try {
    const { pathname, search, hash } = window.location;
    const taken = takeUiParams(search);
    if (taken.mode === null && taken.viewer === null) return;
    if (taken.mode !== null) {
      // Unbound, so the user who loads next takes it over (QA switching on a shared machine).
      state = { ...state, stored: { mode: taken.mode, userId: null }, carried: true };
      persistMode(state.stored);
    }
    if (taken.viewer !== null) {
      state = { ...state, viewer: taken.viewer };
      writeKey(VIEWER_KEY, taken.viewer);
    }
    window.history.replaceState(window.history.state, "", `${pathname}${taken.search}${hash}`);
  } catch {
    // No location / history (tests, React Native): nothing to read.
  }
}

function subscribeLocal(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function noSubscription(): () => void {
  return () => undefined;
}

/** In-tab changes only (`setUiMode`, the user binding). Nothing outside the allowed environments. */
function subscribeViewer(listener: () => void): () => void {
  return isUiNextAllowed() ? subscribeLocal(listener) : noSubscription();
}

function getViewerSnapshot(): boolean {
  return isUiNextAllowed() && state.viewer !== "classic";
}

function getServerSnapshot(): boolean {
  return false;
}

function getServerGate(): UiNextGate {
  return "off";
}

export function getUiMode(): UiMode {
  return state.stored.mode;
}

/**
 * Non-hook twin of `useUiNext` for code outside React (redirect builders): the EFFECTIVE mode, i.e. allowed env,
 * stored "next" bound to this session's user (or unbound and carried by this load), and the portal kill switch
 * (`layout`) not set.
 */
export function isUiNextOn(layout: unknown): boolean {
  return isLocallyOn(state) && !isUiNextKilled(layout);
}

/**
 * The `ui` value an off-host hand-off (the sign-in host: upgrade, manage subscription, create portal) carries, as
 * that host has its own storage: "next" while the new UI is on; "classic" while it is off but this browser holds a
 * record (the user has used the switch, so the other host may hold a stale "next"); null otherwise, so a user who
 * never switched sends exactly what they sent before the programme. Always null outside the allowed environments.
 */
export function uiModeHandoff(layout: unknown): UiMode | null {
  if (!isUiNextAllowed()) return null;
  if (isUiNextOn(layout)) return "next";
  return readKey(MODE_KEY) === null ? null : "classic";
}

export function setUiMode(mode: UiMode): void {
  const stored: StoredUiMode = { mode, userId: state.sessionUserId };
  persistMode(stored);
  commit({ ...state, stored });
}

export function bindUiModeToUser(userId: string): void {
  if (userId === "") return;
  const stored = bindStoredUiMode(state.stored, userId);
  if (stored !== state.stored) persistMode(stored);
  if (stored === state.stored && state.sessionUserId === userId) return;
  commit({ ...state, stored, sessionUserId: userId });
}

/** The signed-in user changed (`userDetails._id`; empty / absent = signed out or not loaded): bind or forget. */
export function syncUiModeUser(userId: string | null | undefined): void {
  if (!isUiNextAllowed()) return;
  if (userId === undefined || userId === null || userId === "") {
    if (state.sessionUserId !== null) commit({ ...state, sessionUserId: null });
    return;
  }
  bindUiModeToUser(userId);
}

function sessionUserOf(root: RootState): string | null {
  const userId = root.userDetails._id;
  return userId === undefined || userId === "" ? null : userId;
}

/** The gate for the store's current state (allowed env, record, the signed-in user, kill switch). */
function gateOf(root: RootState): UiNextGate {
  if (!isUiNextAllowed()) return "off";
  const gate = resolveUiNextGate(state.stored, sessionUserOf(root), state.carried);
  return gate === "on" && isUiNextKilled(root.layout) ? "off" : gate;
}

/**
 * The new UI's gate for this render. Reads the signed-in user and the kill switch from the store in the same
 * snapshot, so a user change never renders one frame against the previous user. Outside the allowed environments it
 * subscribes to nothing. While subscribed it also keeps the stored record bound to the signed-in user.
 */
export function useUiNextGate(): UiNextGate {
  const store = useStore<RootState>();
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!isUiNextAllowed()) return noSubscription();
      syncUiModeUser(store.getState().userDetails._id);
      const unsubscribeStore = store.subscribe(() => {
        syncUiModeUser(store.getState().userDetails._id);
        onChange();
      });
      const unsubscribeLocal = subscribeLocal(onChange);
      return () => {
        unsubscribeStore();
        unsubscribeLocal();
      };
    },
    [store],
  );
  const getSnapshot = () => gateOf(store.getState());
  return useSyncExternalStore(subscribe, getSnapshot, getServerGate);
}

/** True when the new UI renders: allowed env, portal not killed, mode "next", bound to this user. */
export function useUiNext(): boolean {
  return useUiNextGate() === "on";
}

/** The stored viewer mode: "next" unless `?viewer=classic` opted out (`cube.ui.viewer`). */
export function getViewerMode(): UiMode {
  return state.viewer;
}

/**
 * The viewer is not opted out (`cube.ui.viewer` is not "classic"), regardless of the main flag; false outside the
 * allowed environments. Router reads it for its pending decision on the viewer route.
 */
export function useViewerOptIn(): boolean {
  return useSyncExternalStore(subscribeViewer, getViewerSnapshot, getServerSnapshot);
}

/** The viewer follows the UI mode: on with the new UI unless `?viewer=classic` opted out (plan-ds §4.1). */
export function useViewerNext(): boolean {
  const uiNext = useUiNext();
  const viewerOn = useViewerOptIn();
  return viewerFollowsUi(uiNext, viewerOn ? "next" : "classic");
}

loadFromStorage();
applyUrlParams();
syncHtmlAttribute();
